import optins from '.././assets/data/optins.json';
import translations from '../assets/data/optinTranslations.json';
import { IdentityLevel } from '../types/registrationFlows';

export enum SimplifiedIdentityLevel {
  DEPRECATED = 'D',
  LARGE = 'L',
  MEDIUM = 'M',
  SMALL = 'S',
}

type Position = {
  key: string;
  position: number;
  checked: boolean;
};

type OptinEntry = {
  entity: string;
  action: string;
  brand: string;
  identity_level: string;
  context: string;
  positions: Position[];
};

export type Optin = {
  order: number;
  optinKey: string;
  optinText: string;
  checked: boolean;
};

type Translations = { [brand: string]: { [optinKey: string]: string } };

export type GetOptinConfigRequest = {
  action?: string;
  brand?: string;
  identity_level?: IdentityLevel;
  context?: string;
};

export type GetInternalOptinConfigRequest = Omit<GetOptinConfigRequest, 'identity_level'> & {
  identity_level?: SimplifiedIdentityLevel;
};

type OptinSearchFields = (keyof GetInternalOptinConfigRequest)[];

const InternalIdentityLevelMap: { [key in IdentityLevel]: SimplifiedIdentityLevel } = {
  [IdentityLevel.MH2]: SimplifiedIdentityLevel.SMALL,
  [IdentityLevel.MH3]: SimplifiedIdentityLevel.SMALL,
  [IdentityLevel.MH4]: SimplifiedIdentityLevel.MEDIUM,
  [IdentityLevel.MH5]: SimplifiedIdentityLevel.LARGE,
  [IdentityLevel.MH6]: SimplifiedIdentityLevel.MEDIUM,
  [IdentityLevel.MH7]: SimplifiedIdentityLevel.LARGE,
  [IdentityLevel.MH8]: SimplifiedIdentityLevel.MEDIUM,
  [IdentityLevel.MH9]: SimplifiedIdentityLevel.MEDIUM,
};

class OptinService {
  private _defaultOptins: Optin[] = [];
  private _translations: Translations = translations as Translations;

  async getOptinConfig({
    identity_level,
    ...otherConfigParams
  }: GetOptinConfigRequest): Promise<Optin[]> {
    const optinsRequest = {
      ...otherConfigParams,
      identity_level: InternalIdentityLevelMap[identity_level!],
    };
    let foundOptin = this.findOptin(optinsRequest);
    if (!foundOptin) {
      const wildCardedSearches = this.getWildCardSearches(optinsRequest);
      // done with a forloop to prevent further search when found.
      for (const search of wildCardedSearches) {
        foundOptin = this.findOptin(search);
        if (foundOptin) {
          break;
        }
      }
    }
    return foundOptin ? this.mapToOptins(foundOptin) : this._defaultOptins;
  }

  private mapToOptins(foundOptin: OptinEntry): Optin[] {
    // find translation in list of translationentries by brandcode
    return foundOptin.positions
      .sort((p1, p2) => p1.position - p2.position)
      .map((_) => ({
        order: _.position,
        optinKey: _.key,
        optinText: this._translations[foundOptin.brand][_.key] as string,
        checked: _.checked,
      }));
  }

  private findOptin(getOptinRequest: GetInternalOptinConfigRequest): OptinEntry | undefined {
    const optinEntries: OptinEntry[] = optins as OptinEntry[];
    const { action, brand, identity_level, context } = getOptinRequest;
    return optinEntries.find(
      (_) =>
        this.equalsIgnoreCase(_.action, action) &&
        this.equalsIgnoreCase(_.brand, brand) &&
        this.equalsIgnoreCase(_.context, context) &&
        this.equalsIgnoreCase(_.identity_level, identity_level)
    );
  }

  private equalsIgnoreCase(str1: string | undefined, str2: string | undefined): boolean {
    return str1?.toLowerCase() === str2?.toLowerCase();
  }

  private getWildCardSearches(
    givenConfigRequest: GetInternalOptinConfigRequest
  ): GetInternalOptinConfigRequest[] {
    // permutations of all possible searches with wildcards sorted from most specific to the least specific
    const requestFieldNames: OptinSearchFields = ['action', 'brand', 'identity_level', 'context'];
    const permutations = this.getFieldPermutations(requestFieldNames);
    return permutations.map((permutation) =>
      this.createConfigRequests(permutation, givenConfigRequest, requestFieldNames)
    );
  }

  private createConfigRequests(
    permutation: OptinSearchFields,
    givenConfigRequest: GetInternalOptinConfigRequest,
    requestFields: OptinSearchFields
  ): GetInternalOptinConfigRequest {
    const ifNotInPermutationReturnWildcard = (field: keyof GetInternalOptinConfigRequest) => [
      field,
      permutation.includes(field) ? givenConfigRequest[field] : '*',
    ];
    const entries = requestFields.map((field) => ifNotInPermutationReturnWildcard(field));
    return Object.fromEntries(entries);
  }

  private getFieldPermutations(fieldNames: OptinSearchFields): OptinSearchFields[] {
    const permutationsQueue = [fieldNames];
    const permutationsResult: OptinSearchFields[] = [];
    while (permutationsQueue.length > 0) {
      const currentPermutation = permutationsQueue.shift();
      if (currentPermutation) {
        permutationsResult.push(currentPermutation);
        const permutations = this.getFieldPermutationsWithOneElementFixed(
          currentPermutation
        ).filter((_) => _.length > 0);
        const newPermutations = permutations.filter(
          (perm) => !permutationsQueue.some((existingPerm) => existingPerm.join() === perm.join())
        );
        permutationsQueue.push(...newPermutations);
      }
    }
    // filter out duplicates from the permutationsResult
    return permutationsResult;
  }

  private getFieldPermutationsWithOneElementFixed(
    fieldNames: OptinSearchFields
  ): OptinSearchFields[] {
    return fieldNames.map((_, index, arr) => arr.filter((_, i) => i !== index));
  }
}

export const optinService = new OptinService();
